import type { DailyNoteCategoryRow } from '../types/dailyRecordNote';

const PREFIX = 'cp.dailyNoteCategories.v3';

function cacheKey(userId: string): string {
  return `${PREFIX}.${userId}`;
}

export function buildDailyNoteCategoriesVersion(totalCount: number, maxUpdatedAt: string): string {
  return `${totalCount}:${maxUpdatedAt.trim()}`;
}

export type DailyNoteCategoriesCache = {
  version: string;
  categories: DailyNoteCategoryRow[];
};

function isCategoryRow(v: unknown): v is DailyNoteCategoryRow {
  if (!v || typeof v !== 'object') return false;
  const r = v as Record<string, unknown>;
  return (
    typeof r.id === 'string' &&
    typeof r.user_id === 'string' &&
    typeof r.name === 'string' &&
    typeof r.color === 'string' &&
    typeof r.sort_order === 'number'
  );
}

export function loadDailyNoteCategoriesCache(userId: string): DailyNoteCategoriesCache | null {
  if (!userId) return null;
  try {
    const raw = localStorage.getItem(cacheKey(userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<DailyNoteCategoriesCache>;
    const version = typeof parsed.version === 'string' ? parsed.version : '';
    if (!version || !Array.isArray(parsed.categories) || !parsed.categories.every(isCategoryRow)) {
      return null;
    }
    return {
      version,
      categories: parsed.categories.map((c) => ({
        id: c.id,
        user_id: c.user_id,
        name: c.name,
        color: c.color,
        sort_order: c.sort_order,
        created_at: typeof c.created_at === 'string' ? c.created_at : '',
        updated_at: typeof c.updated_at === 'string' ? c.updated_at : '',
        system_key:
          c.system_key === 'general' || c.system_key === 'daily' || c.system_key === 'sticky'
            ? c.system_key
            : null,
      })),
    };
  } catch {
    return null;
  }
}

export function saveDailyNoteCategoriesCache(userId: string, payload: DailyNoteCategoriesCache): void {
  if (!userId || !payload.version) return;
  try {
    localStorage.setItem(cacheKey(userId), JSON.stringify(payload));
  } catch {
    /* ignore quota */
  }
}

export function clearDailyNoteCategoriesCache(userId?: string): void {
  if (typeof localStorage === 'undefined') return;
  try {
    if (userId) {
      localStorage.removeItem(cacheKey(userId));
      return;
    }
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const k = localStorage.key(i);
      if (k && k.startsWith(`${PREFIX}.`)) keys.push(k);
    }
    for (const k of keys) localStorage.removeItem(k);
  } catch {
    /* noop */
  }
}
